import axios from "axios";
import { ElMessage, ElMessageBox } from "element-plus";
import { useUserStore } from "@/store/modules/user";
import { BIZ_CODE, BIZ_CODE_MESSAGE } from "@/constants";
import router from "@/router";
import { refreshAccessToken } from "@/api/auth";

const service = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_API || "/api",
  timeout: 10000,
});

// 主动续期：距过期 60s 前触发；防止多 tab / 多请求重复触发
const REFRESH_AHEAD_MS = 60 * 1000;
let refreshTimer = null;

// 401 并发控制：同一时刻只允许一个续期在跑，其他 401 挂在它完成后一起重试或统一失败
let isRefreshing = false;
let pendingQueue = [];

// 登录态过期全局只提示一次：并发的多个接口同时判定过期时，避免堆叠多个弹窗
let loginExpiredShown = false;

const enqueuePending = (config) =>
  new Promise((resolve, reject) => {
    pendingQueue.push({ config, resolve, reject });
  });

const flushPending = (newToken, error) => {
  const queue = pendingQueue;
  pendingQueue = [];
  queue.forEach(({ config, resolve, reject }) => {
    if (error) {
      reject(error);
      return;
    }
    // 队列重试也标记为已重试，再 401 直接走过期流程，避免循环
    config._authRetried = true;
    config.headers["Authorization"] = `Bearer ${newToken}`;
    resolve(service(config));
  });
};

export const scheduleRefresh = (expiresAt) => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (!expiresAt) return;
  const delay = expiresAt - Date.now() - REFRESH_AHEAD_MS;
  if (delay <= 0) {
    // 已临近过期或过期，立即静默续期
    silentRefresh();
    return;
  }
  refreshTimer = setTimeout(silentRefresh, delay);
};

export const clearAuthTimers = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  // 退出/重登后解除过期与踢线的「只提示一次」锁定
  loginExpiredShown = false;
  kickedOnce = false;
  // 任何等待中的 401 重试都让它失败，调用方会自行处理
  if (pendingQueue.length) {
    flushPending(null, new Error("auth cleared"));
  }
};

const doRefresh = async () => {
  const userStore = useUserStore();
  const refreshToken = userStore.refreshToken;
  if (!refreshToken) return null;
  const res = await refreshAccessToken(refreshToken);
  if (!res.data || !res.data.access_token) return null;
  const expiresIn = Number(res.data.expires_in) || 0;
  const expiresAt = expiresIn > 0 ? Date.now() + expiresIn * 1000 : 0;
  userStore.setToken(res.data.access_token, res.data.refresh_token, expiresAt);
  scheduleRefresh(expiresAt);
  return res.data.access_token;
};

const silentRefresh = async () => {
  if (isRefreshing) return;
  isRefreshing = true;
  try {
    const newToken = await doRefresh();
    if (newToken) {
      flushPending(newToken, null);
    } else {
      flushPending(null, new Error("refresh failed"));
    }
  } catch (e) {
    flushPending(null, e);
  } finally {
    isRefreshing = false;
  }
};

const handleAuthError = async (originalConfig) => {
  const userStore = useUserStore();
  if (!userStore.token && !userStore.refreshToken) {
    throw new Error("not authenticated");
  }
  if (router.currentRoute.value.path === "/login") {
    throw new Error("on login page");
  }
  // 已判定为登录态过期：后续请求直接失败，不再重复续期、也不再重复弹窗
  if (loginExpiredShown) {
    throw new Error("login expired");
  }
  // 只允许重试一次：刷新后带新 token 再 401，说明新 token 仍无效（被踢/后端异常），
  // 不再续期重试，直接走登录态过期流程，防止 401 → 刷新 → 401 无限循环刷接口
  if (originalConfig._authRetried) {
    triggerLoginExpired();
    throw new Error("retry still unauthorized");
  }
  originalConfig._authRetried = true;
  // 已有别的请求在续期：挂到队列，等它成功后自动重试
  if (isRefreshing) {
    return enqueuePending(originalConfig);
  }
  isRefreshing = true;
  try {
    const newToken = await doRefresh();
    if (!newToken) {
      flushPending(null, new Error("refresh failed"));
      triggerLoginExpired();
      throw new Error("refresh failed");
    }
    flushPending(newToken, null);
    originalConfig.headers["Authorization"] = `Bearer ${newToken}`;
    return service(originalConfig);
  } catch (e) {
    flushPending(null, e);
    triggerLoginExpired();
    throw e;
  } finally {
    isRefreshing = false;
  }
};

const triggerLoginExpired = () => {
  // 并发的多个接口都会走到这里，只弹第一个，其余静默失败
  if (loginExpiredShown) return;
  loginExpiredShown = true;
  const userStore = useUserStore();
  ElMessageBox.confirm("登录状态已过期，请重新登录", "系统提示", {
    confirmButtonText: "重新登录",
    cancelButtonText: "取消",
    type: "warning",
  })
    .then(() => {
      clearAuthTimers();
      userStore.logout();
    })
    .catch(() => {
      // 取消后保持锁定：会话仍是过期态，不再反复弹窗打扰
    });
};

// 业务约定：HTTP 500 = 账号已在其他设备登录，强制退出（防并发 500 重复触发）
let kickedOnce = false;
const forceLogout = () => {
  if (kickedOnce) return;
  kickedOnce = true;
  ElMessage.warning("账号已在其他设备登录，请重新登录");
  useUserStore().logout();
};

service.interceptors.request.use(
  (config) => {
    const userStore = useUserStore();
    if (userStore.token) {
      config.headers["Authorization"] = `Bearer ${userStore.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

service.interceptors.response.use(
  (response) => {
    const res = response.data;
    if (res && res.code !== undefined && res.code !== 0) {
      // 档期校验：冲突以 code=30002 返回，由调用方（OrderModal）按 StockConflict 自行判断，
      // 不弹全局 toast，避免「怎么选都冲突」的重复提示
      if (
        response.config.url &&
        response.config.url.includes("/availability/check")
      ) {
        return Promise.reject(
          Object.assign(new Error(res.msg || "Error"), {
            code: res.code,
            data: res.data,
          }),
        );
      }
      handleBizError(res.code, res.msg);
      return Promise.reject(
        Object.assign(new Error(res.msg || "Error"), {
          code: res.code,
          data: res.data,
        }),
      );
    }
    return res;
  },
  async (error) => {
    const status = error.response ? error.response.status : 0;
    if (status === 401) {
      try {
        return await handleAuthError(error.config);
      } catch {
        return Promise.reject(error);
      }
    }
    if (status === 500) {
      // 业务约定：账号已在其他设备登录，强制退出
      forceLogout();
      return Promise.reject(error);
    }
    if (status === 403) {
      ElMessage.error("无权限执行该操作");
      return Promise.reject(error);
    }
    const msg =
      error.response?.data?.msg ||
      error.response?.data?.error_description ||
      error.message ||
      "网络错误";
    ElMessage.error(msg);
    return Promise.reject(error);
  },
);

// 业务码 → 用户提示策略
// 规则：后端错误响应只要带 msg 文本，一律直接显示该文本，不做 code 枚举映射；
// 仅当 msg 为空时才按 BIZ_CODE 枚举兜底提示
const handleBizError = (code, msg) => {
  // 账号已在其他设备登录：强制退出（优先于 msg 展示，保证一定被踢下线）
  if (code === BIZ_CODE.KICKED) {
    forceLogout();
    return;
  }
  if (msg) {
    ElMessage.error(msg);
    return;
  }
  // 登录态过期：确认后清会话重新登录
  if (code === BIZ_CODE.UNLOGIN) {
    triggerLoginExpired();
    return;
  }
  if (code === BIZ_CODE.NO_PERMISSION) {
    ElMessage.error("无权限执行该操作");
    return;
  }
  if (
    code === BIZ_CODE.CROSS_TENANT ||
    code === BIZ_CODE.CROSS_MERCHANT_DEVICE
  ) {
    ElMessage.error("禁止跨租户操作");
    return;
  }
  // 库存/档期类：warn 级别
  if (
    code === BIZ_CODE.STOCK_NOT_ENOUGH ||
    code === BIZ_CODE.STOCK_CONFLICT ||
    code === BIZ_CODE.PACKAGE_COMP_NOT_ENOUGH ||
    code === BIZ_CODE.DEVICE_OCCUPIED
  ) {
    ElMessage.warning(BIZ_CODE_MESSAGE[code]);
    return;
  }
  // 状态机：warn 级别，提示当前状态不允许
  if (
    code === BIZ_CODE.ORDER_STATUS_FORBID ||
    code === BIZ_CODE.ORDER_CANCELED ||
    code === BIZ_CODE.ORDER_ITEM_LOCKED
  ) {
    ElMessage.warning(BIZ_CODE_MESSAGE[code]);
    return;
  }
  // 扫码出库类
  if (
    code === BIZ_CODE.DEVICE_CODE_INVALID ||
    code === BIZ_CODE.DEVICE_CODE_VOID ||
    code === BIZ_CODE.DEVICE_NOT_IN_STOCK ||
    code === BIZ_CODE.MODEL_MISMATCH ||
    code === BIZ_CODE.DEVICE_DUP_SCAN ||
    code === BIZ_CODE.REQUIRED_ACCESSORY_UNDONE
  ) {
    ElMessage.error(BIZ_CODE_MESSAGE[code]);
    return;
  }
  // 验机/结算
  if (
    code === BIZ_CODE.INSPECT_ITEM_EMPTY ||
    code === BIZ_CODE.SETTLE_CONFIRMED
  ) {
    ElMessage.warning(BIZ_CODE_MESSAGE[code]);
    return;
  }
  if (code === BIZ_CODE.BOSS_ONLY) {
    ElMessage.warning("此操作仅老板可执行");
    return;
  }
  // 配置
  if (code === BIZ_CODE.NAME_DUP || code === BIZ_CODE.DISABLED_OBJECT) {
    ElMessage.warning(BIZ_CODE_MESSAGE[code]);
    return;
  }
  // 兜底
  ElMessage.error(BIZ_CODE_MESSAGE[code] || "请求错误");
};

export default service;
